import { useState, useRef} from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Share2,
  Moon,
  Lock,
  Keyboard,
  Sun,
  User,
  LogOut,
  PanelLeftOpen,
  Download,
} from "lucide-react";
import ShareProjectDialog from "./dialogs/ShareProjectDialog";
import { SaveProjectDialog } from "./dialogs/SaveProjectDialog";
import TemplateDialog from "./dialogs/TemplateDialog";
import KeyboardShortcutsDialog from "./dialogs/KeyboardShortcutsDialog";
import ReportCustomizationDialog from "./dialogs/ReportCustomizationDialog";
import ProjectMenu from "./ProjectMenu";
import AuthRequiredModal from "./dialogs/AuthRequiredModal";
import { useAuth } from "../hooks/useAuth";
import { useProjectContext } from "../contexts/ProjectContext";
import { Eye } from "lucide-react";
import { useUIContext } from "../providers/UIProvider";
import { useClickOutside } from "../hooks/useClickOutside";
import { useNotifications } from "../contexts/NotificationContext";
import useStore from "../store/useStore";
import ToolSwitcher from "./ToolSwitcher"; // Import the new ToolSwitcher component

export default function AppBar() {
  // Get nodes from the store at the component level, not inside event handlers
  const nodes = useStore((state) => state.nodes);
  // Helper function to truncate email
  const truncateEmail = (email: string | undefined): string => {
    if (!email) return '';
    
    const [username, domain] = email.split('@');
    if (!domain) return email;
    
    // Show first 4 characters of username + ellipsis + @ + domain
    return username.length > 4
      ? `${username.substring(0, 4)}...@${domain}`
      : email;
  };
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isShareButtonHovered, setIsShareButtonHovered] = useState(false);
  const [isDownloadButtonHovered, setIsDownloadButtonHovered] = useState(false);
  const { project, saveProject, isPreviewMode } = useProjectContext();
  const { isAdmin, logout, isAuthenticated, user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useUIContext();
  const { showNotification } = useNotifications();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(
    () => {
      setIsUserMenuOpen(false);
    },
    [],
    [userMenuRef]
  );

  const handleSaveProject = async (name: string, description: string) => {
    if (project) {
      project.name = name;
      project.description = description;
      await saveProject();
      setShowShareDialog(true);
    }
  };

  return (
    <div
      className="h-14 px-5 flex items-center justify-between bg-gray-900 dark:bg-gray-950 text-white"
    >
      {/* Left section - Tool Switcher */}
      <div className="flex items-center gap-2">
        <ToolSwitcher isAdmin={isAdmin} />
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-200 dark:text-yellow-200">
          Alpha v5.0.3
        </span>
      </div>

      {/* Center section - Project Menu or Admin Title */}
      {window.location.pathname === '/admin' ? (
        <div className="text-xl text-white">Admin Panel</div>
      ) : (
        <ProjectMenu />
      )}

      {/* Right section */}
      <div className="flex items-center gap-2">
        {isPreviewMode && (
          <div className="flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-200 rounded-md">
            <Eye className="h-4 w-4" />
            <span className="text-sm">Preview Mode</span>
          </div>
        )}
        {window.location.pathname !== '/admin' && (
          <>
            {/* Share Project Button - Secondary */}
            <div className="relative group">
              <motion.button
                whileHover={{ scale: !project || isPreviewMode || (isAuthenticated && project?.id === 'unsaved-project') ? 1 : 1.00 }}
                whileTap={{ scale: !project || isPreviewMode || (isAuthenticated && project?.id === 'unsaved-project') ? 1 : 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                className={`text-sm font-medium py-1.5 px-4 rounded flex items-center transition duration-300 ease-in-out ${
                  !project || isPreviewMode || (isAuthenticated && project?.id === 'unsaved-project') ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
                onMouseEnter={() => setIsShareButtonHovered(true)}
                onMouseLeave={() => setIsShareButtonHovered(false)}
                onClick={() => {
                  if (isPreviewMode) {
                    showNotification('info', 'You cannot share projects in preview mode');
                    return;
                  }
                  if (isAuthenticated && project?.id === 'unsaved-project') {
                    showNotification('info', 'Please save your project before sharing');
                    setShowSaveDialog(true);
                    return;
                  }
                  if (!isAuthenticated) {
                    setShowAuthRequiredModal(true);
                  } else if (project) {
                    setShowShareDialog(true);
                  } else {
                    setShowSaveDialog(true);
                  }
                }}
                disabled={isPreviewMode || (isAuthenticated && project?.id === 'unsaved-project')}
              >
                {
                  !project || isPreviewMode || (isAuthenticated && project?.id === 'unsaved-project') 
                    ? <Lock className="h-4 w-4 mr-2"/> 
                    : <Share2 className="h-4 w-4 mr-2"/>
                }
                {isAuthenticated ? "Share Project" : "Save or Share Project"}
              </motion.button>
              
              {/* Tooltip that appears on hover when button is disabled */}
              <AnimatePresence>
                {isShareButtonHovered && (isAuthenticated && project?.id === 'unsaved-project') && (
                  <motion.div 
                    className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none z-50"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    Save your project first to enable sharing
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-800"></div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Get Report Button - Primary */}
            <div className="relative group">
              <motion.button
                whileHover={{ scale: !project || isPreviewMode ? 1 : 1.00 }}
                whileTap={{ scale: !project || isPreviewMode ? 1 : 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                className={`text-white text-sm font-medium py-1.5 px-3 rounded flex items-center transition duration-300 ease-in-out ${
                  !project || isPreviewMode ? 'bg-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-primary-700'
                }`}
                onMouseEnter={() => setIsDownloadButtonHovered(true)}
                onMouseLeave={() => setIsDownloadButtonHovered(false)}
                onClick={() => {
                  if (isPreviewMode) {
                    showNotification('info', 'You cannot generate reports in preview mode');
                    return;
                  }
                  if (!project) {
                    showNotification('info', 'No project to generate report from');
                    return;
                  }
                  if (!nodes || nodes.length === 0) {
                    showNotification('error', 'The diagram is empty. Please add some nodes first.');
                    return;
                  }
                  
                  setShowReportDialog(true);
                }}
                disabled={!project || isPreviewMode}
              >
                <Download className="h-4 w-4 mr-2" />
                Get Report
              </motion.button>
              
              {/* Tooltip for disabled state */}
              <AnimatePresence>
                {isDownloadButtonHovered && !project && (
                  <motion.div 
                    className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none z-50"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    Create a project first to generate a report
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-800"></div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}

        {/* User Avatar and Menu */}
        <div className="relative" ref={userMenuRef}>
          <motion.button
            className="p-0"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            whileHover={{ scale: 1.00 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div 
              className="rounded-full h-8 w-8 bg-gray-700 hover:bg-gray-600 flex items-center justify-center"
              transition={{ duration: 0.2 }}
            >
              <User className="h-5 w-5 text-gray-300" />
            </motion.div>
          </motion.button>

          {isUserMenuOpen && (
            <div
              className="fixed inset-0 bg-black transition-opacity duration-200 ease-in-out bg-opacity-0 z-40"
              onClick={() => setIsUserMenuOpen(false)}
            />
          )}
          <AnimatePresence>
            {isUserMenuOpen && (
              <motion.div 
                className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-900 border border-gray-800 dark:bg-gray-950 ring-1 ring-black ring-opacity-5 z-50"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
              {isAuthenticated && (
                <div className="px-4 py-2">
                  <div className="text-xs text-gray-400">Logged in as</div>
                  <div className="text-sm text-gray-200">
                    {truncateEmail(user?.email)}
                  </div>
                </div>
              )}
              <div className="py-1" role="menu">
                <button
                  onClick={() => setShowKeyboardShortcuts(true)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                  role="menuitem"
                >
                  <Keyboard className="h-4 w-4 mr-2 inline-block" />
                  Hotkeys
                </button>

                <button
                  onClick={toggleDarkMode}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                  role="menuitem"
                >
                  {isDarkMode ? (
                    <Sun className="h-4 w-4 mr-2 inline-block" />
                  ) : (
                    <Moon className="h-4 w-4 mr-2 inline-block" />
                  )}
                  {isDarkMode ? "Light Mode" : "Dark Mode"}
                </button>

                {isAuthenticated ? (
                  <>
                    {isAdmin && window.location.pathname !== '/admin' && (
                      <Link
                        to="/admin"
                        className="block px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                        role="menuitem"
                      >
                        <PanelLeftOpen className="h-4 w-4 mr-2 inline-block" />
                        Admin Panel
                      </Link>
                    )}

                    <button
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                      role="menuitem"
                    >
                      <LogOut className="h-4 w-4 mr-2 inline-block" />
                      Log out
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowAuthRequiredModal(true);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                    role="menuitem"
                  >
                    <User className="h-4 w-4 mr-2 inline-block" />
                    Log in
                  </button>
                )}

              </div>
            </motion.div>
          )}
          </AnimatePresence>
        </div>
      </div>

      {/* Dialogs */}
      <AnimatePresence>
        {showAuthRequiredModal && (
          <AuthRequiredModal 
            key="auth-required-modal"
            onClose={() => setShowAuthRequiredModal(false)} 
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showShareDialog && project && (
          <ShareProjectDialog 
            key="share-project-dialog"
            onClose={() => setShowShareDialog(false)} 
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSaveDialog && (
          <SaveProjectDialog
            key="save-project-dialog"
            open={showSaveDialog}
            onClose={() => setShowSaveDialog(false)}
            onSave={handleSaveProject}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showTemplateDialog && (
          <TemplateDialog 
            key="template-dialog"
            onClose={() => setShowTemplateDialog(false)} 
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showKeyboardShortcuts && (
          <KeyboardShortcutsDialog 
            key="keyboard-shortcuts-dialog"
            onClose={() => setShowKeyboardShortcuts(false)} 
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showReportDialog && (
          <ReportCustomizationDialog
            key="report-customization-dialog"
            onClose={() => setShowReportDialog(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
